import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View, Text, ScrollView, Pressable, StyleSheet, ActivityIndicator, Platform,
} from 'react-native';
import {
  VictoryChart, VictoryLine, VictoryAxis, VictoryBar,
  VictoryScatter, VictoryGroup,
} from 'victory-native';
import { format } from 'date-fns';
import LogDateRangeFilter from '../controls/LogDateRangeFilter';
import { Tabs, TabScreen, TabsProvider } from 'react-native-paper-tabs';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { useAuth } from '../../../../providers/AuthProvider';
import { useClient } from '../../../../providers/ClientProvider';
import { useNav } from '../../../../providers/NavProvider';
import { logStyles } from '../../LogControllers/LogStyles';
import { findProjectParent } from '../service/dataOperations';
import SingleBarChartComp from '../charts/fenceline_barChart_builder';
import MultiBarChartComp from '../charts/fenceline_barChart_builder_multiple';
import StackedBarChartComp from '../charts/fencelineStackedBarChartComp';
import FencelinePieChartComp from '../charts/fencelinePieChartComp';
import Windrose from '../charts/fenceline_windroseChart';
import { IconButton } from 'react-native-paper';
import { toPng } from 'html-to-image';
import { handleExportClick } from '../../ConsolidatedLogs/ConsolidatedController';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Stack, Typography, Tooltip,
} from '@mui/material';

// ─── Types ────────────────────────────────────────────────────────────────────

type TelemetryRow = Record<string, string | number | null>;

interface StationData {
  stationName: string;
  rows: TelemetryRow[];
  captureRate: Record<string, number>;
}


interface Props {
  siteId: string;
  logTitle: string;
  isDashboard?: boolean;
}

interface ThresholdEntry { limit: number; unit: string; label: string; }

interface ThresholdsConfig {
  Avg_PM10:           ThresholdEntry;
  Avg_PM2_5:          ThresholdEntry;
  DayAvg_NoiseLAeq:   ThresholdEntry;
  NightAvg_NoiseLAeq: ThresholdEntry;
  Max_NoiseLASmax:    ThresholdEntry;
  Avg_VibV1:          ThresholdEntry;
}

// ─── Default thresholds (fallback when complianceConfig unavailable) ──────────

const DEFAULT_THRESHOLDS: ThresholdsConfig = {
  Avg_PM10:           { limit: 50,  unit: 'µg/m³', label: 'PM10 24hr'        },
  Avg_PM2_5:          { limit: 25,  unit: 'µg/m³', label: 'PM2.5 24hr'       },
  DayAvg_NoiseLAeq:   { limit: 65,  unit: 'dB(A)', label: 'Day Noise LAeq'   },
  NightAvg_NoiseLAeq: { limit: 55,  unit: 'dB(A)', label: 'Night Noise LAeq' },
  Max_NoiseLASmax:    { limit: 80,  unit: 'dB(A)', label: 'LAFmax'           },
  Avg_VibV1:          { limit: 5.0, unit: 'mm/s',  label: 'Vibration'        },
};

// ─── ComplianceConfig → ThresholdsConfig mapper ───────────────────────────────
//
// complianceConfig keys (from api/config/${projectId}_complianceConfig):
//   pm10, pm2p5, vibration — scalar numbers
//   noise.Day/Night[type] — nested; OR flat noiseDay/noiseNight for fenceline

function mapComplianceConfig(cfg: any): ThresholdsConfig {
  const d = DEFAULT_THRESHOLDS;
  if (!cfg || typeof cfg !== 'object') return d;

  function scalarOr(val: unknown, fallback: number): number {
    return typeof val === 'number' && isFinite(val) ? val : fallback;
  }

  function noiseLimit(period: 'Day' | 'Night', fallback: number): number {
    // Flat fenceline-specific keys take priority
    const flatKeys = period === 'Day'
      ? ['noiseDay', 'noiseDayLAeq']
      : ['noiseNight', 'noiseNightLAeq'];
    for (const k of flatKeys) {
      if (typeof cfg[k] === 'number') return cfg[k];
    }
    // Nested noise object — prefer Sensitive → Category D → Roadsides → first value
    const nested = cfg.noise?.[period];
    if (!nested) return fallback;
    if (typeof nested === 'number') return nested;
    for (const key of ['Sensitive', 'Category D', 'Roadsides']) {
      if (typeof nested[key] === 'number') return nested[key];
    }
    const first = Object.values(nested).find(v => typeof v === 'number');
    return typeof first === 'number' ? first : fallback;
  }

  return {
    Avg_PM10:           { ...d.Avg_PM10,           limit: scalarOr(cfg.pm10,         d.Avg_PM10.limit)           },
    Avg_PM2_5:          { ...d.Avg_PM2_5,          limit: scalarOr(cfg.pm2p5,        d.Avg_PM2_5.limit)          },
    DayAvg_NoiseLAeq:   { ...d.DayAvg_NoiseLAeq,   limit: noiseLimit('Day',   d.DayAvg_NoiseLAeq.limit)         },
    NightAvg_NoiseLAeq: { ...d.NightAvg_NoiseLAeq, limit: noiseLimit('Night', d.NightAvg_NoiseLAeq.limit)       },
    Max_NoiseLASmax:    { ...d.Max_NoiseLASmax,     limit: scalarOr(cfg.noiseLASmax,  d.Max_NoiseLASmax.limit)   },
    Avg_VibV1:          { ...d.Avg_VibV1,           limit: scalarOr(cfg.vibration,    d.Avg_VibV1.limit)         },
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────


const DIRECTIONS_16 = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];

const SPEED_RANGES = [
  { start: 0,   end: 0.5,  color: '#1f77b4' },
  { start: 0.5, end: 2,    color: '#aec7e8' },
  { start: 2,   end: 4,    color: '#ff7f0e' },
  { start: 4,   end: 6,    color: '#2ca02c' },
  { start: 6,   end: 8,    color: '#98df8a' },
  { start: 8,   end: 10,   color: '#d62728' },
  { start: 10,  end: 9999, color: '#ff9896' },
];

const WIND_DIRECTION_BINS = [
  { label: 'N',   start: 348.75, end: 11.25  }, { label: 'NNE', start: 11.25,  end: 33.75  },
  { label: 'NE',  start: 33.75,  end: 56.25  }, { label: 'ENE', start: 56.25,  end: 78.75  },
  { label: 'E',   start: 78.75,  end: 101.25 }, { label: 'ESE', start: 101.25, end: 123.75 },
  { label: 'SE',  start: 123.75, end: 146.25 }, { label: 'SSE', start: 146.25, end: 168.75 },
  { label: 'S',   start: 168.75, end: 191.25 }, { label: 'SSW', start: 191.25, end: 213.75 },
  { label: 'SW',  start: 213.75, end: 236.25 }, { label: 'WSW', start: 236.25, end: 258.75 },
  { label: 'W',   start: 258.75, end: 281.25 }, { label: 'WNW', start: 281.25, end: 303.75 },
  { label: 'NW',  start: 303.75, end: 326.25 }, { label: 'NNW', start: 326.25, end: 348.75 },
];

// ─── Utility helpers ──────────────────────────────────────────────────────────

function groupByStation(rows: TelemetryRow[]): Record<string, TelemetryRow[]> {
  return rows.reduce<Record<string, TelemetryRow[]>>((acc, row) => {
    const key = row.StationName as string;
    acc[key] = acc[key] ?? [];
    acc[key].push(row);
    return acc;
  }, {});
}

function calcCaptureRate(columns: string[], rows: TelemetryRow[]): Record<string, number> {
  const metricCols = columns.filter(c => c !== 'StationName' && c !== 'SummaryDate');
  return Object.fromEntries(
    metricCols.map(col => {
      const valid = rows.filter(r => r[col] !== null).length;
      return [col, rows.length > 0 ? Math.round((valid / rows.length) * 100) : 0];
    })
  );
}

function buildExceedanceSummary(station: StationData, thresholds: ThresholdsConfig) {
  return (Object.entries(thresholds) as [keyof ThresholdsConfig, ThresholdEntry][]).map(([col, cfg]) => {
    const measured = station.rows.filter(r => r[col] !== null);
    const exceeded = measured.filter(r => (r[col] as number) > cfg.limit);
    const maxVal = measured.length > 0 ? Math.max(...measured.map(r => r[col] as number)) : 0;
    return {
      parameter: cfg.label, daysMeasured: measured.length,
      captureRate: station.captureRate[col] ?? 0,
      exceedances: exceeded.length, maxValue: maxVal, limit: cfg.limit, unit: cfg.unit,
    };
  });
}

function formatDateTick(dateStr: string): string {
  try { return format(new Date(dateStr), 'dd MMM'); } catch { return dateStr; }
}

function monthYearKey(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleString('default', { month: 'short' })}-${d.getFullYear()}`;
}

function fmt(v: number | null, dp = 2): string {
  return v === null ? '—' : v.toFixed(dp);
}

function pct(n: number, total: number): string {
  return total > 0 ? `${Math.round((n / total) * 100)}%` : '0%';
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildTelemetryUrl(clientAPIURL: string, projectId: string): string {
  const end = new Date();
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 5);
  const params = new URLSearchParams({
    projectId,
    startDate: toLocalDateStr(start),
    endDate:   toLocalDateStr(end),
  });
  return `${clientAPIURL}api/fenceline/daily-summary?${params.toString()}`;
}

// ─── Chart data builders ──────────────────────────────────────────────────────

function buildSingleBarData(rows: TelemetryRow[], field: string) {
  const grouped: Record<string, number[]> = {};
  rows.forEach(r => {
    if (!r.SummaryDate || r[field] === null) return;
    const d = r.SummaryDate as string;
    grouped[d] = grouped[d] ?? [];
    grouped[d].push(r[field] as number);
  });
  const sortedDates = Object.keys(grouped).sort();
  const labels = sortedDates.map(d => formatDateTick(d));
  const colorFn = scaleOrdinal(schemeCategory10);
  return {
    labels,
    datasets: [{
      data: sortedDates.map(d => Math.max(...grouped[d])),
      strokeWidth: 2,
      color: (opacity = 1) => colorFn('0') + Math.round(opacity * 255).toString(16),
    }],
  };
}

function buildMultiBarData(stations: StationData[], field: string) {
  const allDates = [...new Set(
    stations.flatMap(s => s.rows.map(r => r.SummaryDate as string).filter(Boolean))
  )].sort();
  const formattedDates = allDates.map(d => formatDateTick(d));
  const colorFn = scaleOrdinal(schemeCategory10);
  const datasets = stations.map((s, idx) => {
    const rowMap = Object.fromEntries(s.rows.map(r => [r.SummaryDate, r[field]]));
    return {
      name: s.stationName,
      data: allDates.map((date, i) => ({
        x: formattedDates[i],
        y: rowMap[date] !== null && rowMap[date] !== undefined ? rowMap[date] as number : 0,
      })),
      strokeWidth: 2,
      color: (opacity = 1) => colorFn(String(idx)) + Math.round(opacity * 255).toString(16),
    };
  });
  return { labels: formattedDates, datasets };
}

function buildMonthlyExceedances(rows: TelemetryRow[], field: string, threshold: number) {
  const grouped: Record<string, { exc: number; nonExc: number }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate || r[field] === null) return;
    const key = monthYearKey(r.SummaryDate as string);
    grouped[key] = grouped[key] ?? { exc: 0, nonExc: 0 };
    if ((r[field] as number) > threshold) grouped[key].exc++; else grouped[key].nonExc++;
  });
  const labels = Object.keys(grouped);
  return {
    labels,
    datasets: [
      { name: 'Non-Exceedances', data: labels.map((l, i) => ({ x: labels[i], y: grouped[l].nonExc })), strokeWidth: 2, color: (opacity = 1) => `rgba(0,128,0,${opacity})` },
      { name: 'Exceedances',     data: labels.map((l, i) => ({ x: labels[i], y: grouped[l].exc })),    strokeWidth: 2, color: (opacity = 1) => `rgba(255,69,0,${opacity})`  },
    ],
  };
}

function buildPieData(rows: TelemetryRow[], field: string, threshold: number) {
  const measured = rows.filter(r => r[field] !== null);
  const exc = measured.filter(r => (r[field] as number) > threshold).length;
  return [
    { name: 'Non-Exceedances', value: measured.length - exc, color: '#32CD32' },
    { name: 'Exceedances',     value: exc,                    color: '#FF6347' },
  ];
}

function buildNoiseMonthlyExceedances(rows: TelemetryRow[], thresholdDay: number, thresholdNight: number) {
  const grouped: Record<string, { exc: number; nonExc: number }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate) return;
    const key = monthYearKey(r.SummaryDate as string);
    grouped[key] = grouped[key] ?? { exc: 0, nonExc: 0 };
    if (r.DayAvg_NoiseLAeq !== null) (r.DayAvg_NoiseLAeq as number) > thresholdDay ? grouped[key].exc++ : grouped[key].nonExc++;
    if (r.NightAvg_NoiseLAeq !== null) (r.NightAvg_NoiseLAeq as number) > thresholdNight ? grouped[key].exc++ : grouped[key].nonExc++;
  });
  const labels = Object.keys(grouped);
  return {
    labels,
    datasets: [
      { name: 'Non-Exceedances', data: labels.map((l, i) => ({ x: labels[i], y: grouped[l].nonExc })), strokeWidth: 2, color: (opacity = 1) => `rgba(0,128,0,${opacity})` },
      { name: 'Exceedances',     data: labels.map((l, i) => ({ x: labels[i], y: grouped[l].exc })),    strokeWidth: 2, color: (opacity = 1) => `rgba(255,69,0,${opacity})`  },
    ],
  };
}

function buildNoisePieData(rows: TelemetryRow[], thresholdDay: number, thresholdNight: number) {
  let exc = 0, nonExc = 0;
  rows.forEach(r => {
    if (r.DayAvg_NoiseLAeq !== null) (r.DayAvg_NoiseLAeq as number) > thresholdDay ? exc++ : nonExc++;
    if (r.NightAvg_NoiseLAeq !== null) (r.NightAvg_NoiseLAeq as number) > thresholdNight ? exc++ : nonExc++;
  });
  return [
    { name: 'Non-Exceedances', value: nonExc, color: '#32CD32' },
    { name: 'Exceedances',     value: exc,    color: '#FF6347' },
  ];
}

function buildWindroseData(rows: TelemetryRow[]): Record<string, number[]> {
  const binnedData: Record<string, number[]> = {};
  DIRECTIONS_16.forEach(dir => { binnedData[dir] = Array(SPEED_RANGES.length).fill(0); });
  rows.forEach(row => {
    if (!row.Avg_WindDirection) return;
    try {
      // API format: { Direction, Speed1..Speed7 (% of DataCount), DataCount }
      const sectors: { Direction: string; Speed1: number; Speed2: number; Speed3: number; Speed4: number; Speed5: number; Speed6: number; Speed7: number; DataCount: number }[] =
        JSON.parse(row.Avg_WindDirection as string);
      sectors.forEach(s => {
        if (!binnedData[s.Direction]) return;
        const pcts = [s.Speed1, s.Speed2, s.Speed3, s.Speed4, s.Speed5, s.Speed6, s.Speed7];
        pcts.forEach((pct, idx) => {
          if (idx < SPEED_RANGES.length) {
            binnedData[s.Direction][idx] += (pct / 100) * s.DataCount;
          }
        });
      });
    } catch { /* ignore */ }
  });
  return binnedData;
}

// ─── Table data builders ──────────────────────────────────────────────────────

function buildDailyTableData(stations: StationData[], field: string) {
  const allDates = [...new Set(
    stations.flatMap(s => s.rows.filter(r => r.SummaryDate).map(r => r.SummaryDate as string))
  )].sort();
  return allDates.map(date => {
    const values: Record<string, number | null> = {};
    stations.forEach(s => {
      const row = s.rows.find(r => r.SummaryDate === date);
      values[s.stationName] = row ? (row[field] as number | null) : null;
    });
    const nonNull = Object.values(values).filter(v => v !== null) as number[];
    return {
      date,
      stationValues: values,
      max:  nonNull.length ? Math.max(...nonNull) : null,
      min:  nonNull.length ? Math.min(...nonNull) : null,
      avg:  nonNull.length ? nonNull.reduce((a, b) => a + b, 0) / nonNull.length : null,
    };
  });
}

function buildMonthlyConformanceData(rows: TelemetryRow[], field: string, threshold: number) {
  const grouped: Record<string, { total: number; conform: number }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate || r[field] === null) return;
    const key = monthYearKey(r.SummaryDate as string);
    grouped[key] = grouped[key] ?? { total: 0, conform: 0 };
    grouped[key].total++;
    if ((r[field] as number) <= threshold) grouped[key].conform++;
  });
  return Object.entries(grouped).map(([month, d]) => ({
    month, total: d.total, conform: d.conform,
    conformPct: pct(d.conform, d.total),
    nonConform: d.total - d.conform, nonConformPct: pct(d.total - d.conform, d.total),
  }));
}

function buildNoiseMonthlyConformanceData(rows: TelemetryRow[], dayThreshold: number, nightThreshold: number) {
  const grouped: Record<string, {
    totalDay: number; conformDay: number; totalNight: number; conformNight: number;
  }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate) return;
    const key = monthYearKey(r.SummaryDate as string);
    grouped[key] = grouped[key] ?? { totalDay: 0, conformDay: 0, totalNight: 0, conformNight: 0 };
    if (r.DayAvg_NoiseLAeq !== null) {
      grouped[key].totalDay++;
      if ((r.DayAvg_NoiseLAeq as number) <= dayThreshold) grouped[key].conformDay++;
    }
    if (r.NightAvg_NoiseLAeq !== null) {
      grouped[key].totalNight++;
      if ((r.NightAvg_NoiseLAeq as number) <= nightThreshold) grouped[key].conformNight++;
    }
  });
  return Object.entries(grouped).map(([month, d]) => {
    const total = d.totalDay + d.totalNight;
    const conform = d.conformDay + d.conformNight;
    return {
      month, total, conform,
      conformPct: pct(conform, total), nonConform: total - conform, nonConformPct: pct(total - conform, total),
      conformDay: d.conformDay, conformDayPct: pct(d.conformDay, d.totalDay),
      nonConformDay: d.totalDay - d.conformDay, nonConformDayPct: pct(d.totalDay - d.conformDay, d.totalDay),
      conformNight: d.conformNight, conformNightPct: pct(d.conformNight, d.totalNight),
      nonConformNight: d.totalNight - d.conformNight, nonConformNightPct: pct(d.totalNight - d.conformNight, d.totalNight),
    };
  });
}

function buildNoiseLAeqSummaryData(rows: TelemetryRow[], thresholds: ThresholdsConfig) {
  const grouped: Record<string, {
    allNoise: number[]; dayNoise: number[]; nightNoise: number[];
    startDate: string; endDate: string;
  }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate) return;
    const key = monthYearKey(r.SummaryDate as string);
    if (!grouped[key]) grouped[key] = { allNoise: [], dayNoise: [], nightNoise: [], startDate: r.SummaryDate as string, endDate: r.SummaryDate as string };
    if ((r.SummaryDate as string) < grouped[key].startDate) grouped[key].startDate = r.SummaryDate as string;
    if ((r.SummaryDate as string) > grouped[key].endDate)   grouped[key].endDate   = r.SummaryDate as string;
    if (r.Avg_NoiseLAeq      !== null) grouped[key].allNoise.push(r.Avg_NoiseLAeq     as number);
    if (r.DayAvg_NoiseLAeq   !== null) grouped[key].dayNoise.push(r.DayAvg_NoiseLAeq  as number);
    if (r.NightAvg_NoiseLAeq !== null) grouped[key].nightNoise.push(r.NightAvg_NoiseLAeq as number);
  });
  return Object.entries(grouped).map(([month, d]) => ({
    month,
    dateRange:    `${formatDateTick(d.startDate)} – ${formatDateTick(d.endDate)}`,
    totalSamples: d.allNoise.length,
    totalMin:     d.allNoise.length   ? Math.min(...d.allNoise)   : null,
    totalMax:     d.allNoise.length   ? Math.max(...d.allNoise)   : null,
    daySamples:   d.dayNoise.length,
    dayMin:       d.dayNoise.length   ? Math.min(...d.dayNoise)   : null,
    dayMax:       d.dayNoise.length   ? Math.max(...d.dayNoise)   : null,
    dayLimits:    `${thresholds.DayAvg_NoiseLAeq.limit} ${thresholds.DayAvg_NoiseLAeq.unit}`,
    nightSamples: d.nightNoise.length,
    nightMin:     d.nightNoise.length ? Math.min(...d.nightNoise) : null,
    nightMax:     d.nightNoise.length ? Math.max(...d.nightNoise) : null,
    nightLimits:  `${thresholds.NightAvg_NoiseLAeq.limit} ${thresholds.NightAvg_NoiseLAeq.unit}`,
  }));
}

function buildPeriodSummaryData(rows: TelemetryRow[], field: string, threshold: number) {
  const measured = rows.filter(r => r.SummaryDate && r[field] !== null);
  const conform  = measured.filter(r => (r[field] as number) <= threshold);
  const dates    = measured.map(r => r.SummaryDate as string).sort();
  return {
    startDate: dates[0] ?? null,
    endDate:   dates[dates.length - 1] ?? null,
    total:       measured.length,
    conform:     conform.length,
    nonConform:  measured.length - conform.length,
    conformPct:    pct(conform.length, measured.length),
    nonConformPct: pct(measured.length - conform.length, measured.length),
  };
}

function buildNoisePeriodSummaryData(rows: TelemetryRow[], dayThreshold: number, nightThreshold: number) {
  const withDate = rows.filter(r => r.SummaryDate);
  const dates = withDate.map(r => r.SummaryDate as string).sort();
  let totalDay = 0, conformDay = 0, totalNight = 0, conformNight = 0;
  withDate.forEach(r => {
    if (r.DayAvg_NoiseLAeq !== null) {
      totalDay++;
      if ((r.DayAvg_NoiseLAeq as number) <= dayThreshold) conformDay++;
    }
    if (r.NightAvg_NoiseLAeq !== null) {
      totalNight++;
      if ((r.NightAvg_NoiseLAeq as number) <= nightThreshold) conformNight++;
    }
  });
  const total  = totalDay + totalNight;
  const conform = conformDay + conformNight;
  return {
    startDate: dates[0] ?? null,
    endDate:   dates[dates.length - 1] ?? null,
    total, conform, nonConform: total - conform,
    conformPct: pct(conform, total), nonConformPct: pct(total - conform, total),
    conformDay, nonConformDay: totalDay - conformDay,
    conformDayPct: pct(conformDay, totalDay), nonConformDayPct: pct(totalDay - conformDay, totalDay),
    conformNight, nonConformNight: totalNight - conformNight,
    conformNightPct: pct(conformNight, totalNight), nonConformNightPct: pct(totalNight - conformNight, totalNight),
  };
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCSV(headers: string[], rows: (string | number | null)[][], filename: string) {
  if (Platform.OS !== 'web') return;
  const lines = [headers, ...rows].map(row =>
    row.map(cell => `"${cell ?? ''}"`).join(',')
  ).join('\n');
  const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Station selector ─────────────────────────────────────────────────────────

function StationSelector({ stations, selected, onSelect }: {
  stations: StationData[]; selected: string; onSelect: (name: string) => void;
}) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      {['All', ...stations.map(s => s.stationName)].map(opt => (
        <Pressable key={opt} onPress={() => onSelect(opt)} style={[styles.chip, selected === opt && styles.chipActive]}>
          <Text style={[styles.chipText, selected === opt && styles.chipTextActive]}>{opt}</Text>
        </Pressable>
      ))}
    </ScrollView>
  );
}

// ─── Data Capture Warning Badge (collapsible) ────────────────────────────────

function DataCaptureWarningBadge({ captureRate }: { captureRate: Record<string, number> }) {
  const [expanded, setExpanded] = useState(false);
  const low = Object.entries(captureRate).filter(([, p]) => p < 85);
  if (low.length === 0) return null;
  return (
    <View>
      <Pressable onPress={() => setExpanded(e => !e)} style={styles.warnBadge}>
        <Text style={styles.warnBadgeText}>⚠  {low.length} low capture{low.length > 1 ? 's' : ''} {expanded ? '▲' : '▼'}</Text>
      </Pressable>
      {expanded && (
        <View style={styles.warnExpanded}>
          {low.map(([col, p]) => (
            <Text key={col} style={styles.warnExpandedText}>{col}: {p}% — results may be inadmissible</Text>
          ))}
        </View>
      )}
    </View>
  );
}

function ChartTitle({ title }: { title: string }) {
  return <Text style={styles.chartTitle}>{title}</Text>;
}

// ─── Column definition types ──────────────────────────────────────────────────
// Adding a new column = add one entry here. Headers auto-update in JSX render
// and CSV export — no other changes needed.

type ColDef<T> = { header: string; get: (row: T) => any; width?: number };
type ColGroup<T> = { group: string | null; cols: ColDef<T>[] };

function flatHeaders<T>(groups: ColGroup<T>[]): string[] {
  return groups.flatMap(g => g.cols.map(c => g.group ? `${g.group} - ${c.header}` : c.header));
}

function flatRow<T>(groups: ColGroup<T>[], row: T): any[] {
  return groups.flatMap(g => g.cols.map(c => c.get(row)));
}

// ─── TableToolbar ─────────────────────────────────────────────────────────────

function TableToolbar({ title, onExport }: { title: string; onExport?: () => void }) {
  return (
    <View style={styles.tableToolbar}>
      <ChartTitle title={title} />
      {Platform.OS === 'web' && onExport && (
        <Pressable style={styles.exportBtn} onPress={onExport}>
          <Text style={styles.exportBtnText}>Export Data</Text>
        </Pressable>
      )}
    </View>
  );
}

// ─── Chart export infrastructure (fenceline-only, no impact on other components) ─

const safeId = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

async function exportFencelineChart(
  wrapperId: string,
  baseName: string,
  format: 'png' | 'svg',
  mode: 'graph' | 'graph+data',
  exportRows: any[],
) {
  if (typeof document === 'undefined') return;
  const safe = baseName.replace(/[\\/:*?"<>|]+/g, '-').trim();
  const wrapper = document.getElementById(wrapperId);
  if (!wrapper) { console.error('[FencelineExport] Element not found:', wrapperId); return; }

  if (format === 'svg') {
    const svgEl = wrapper.querySelector('svg') as SVGElement | null;
    if (!svgEl) { console.error('[FencelineExport] SVG not found in:', wrapperId); return; }
    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${safe}.svg`; a.click();
    URL.revokeObjectURL(url);
  } else {
    try {
      // Capture the SVG directly (not the scroll container) so the full chart width is preserved.
      // toPng on the wrapper clips at the scroll viewport; the SVG element holds the full rendered chart.
      const svgEl = wrapper.querySelector('svg') as SVGElement | null;
      const captureTarget = (svgEl ?? wrapper) as any;
      const dataUrl = await toPng(captureTarget, {
        pixelRatio: 2, backgroundColor: '#fff',
      });
      const a = document.createElement('a'); a.href = dataUrl; a.download = `${safe}.png`; a.click();
    } catch (e) { console.error('[FencelineExport] PNG capture failed:', e); }
  }

  if (mode === 'graph+data' && exportRows.length > 0) {
    handleExportClick(exportRows, `${safe}_Data.xlsx`);
  }
}

// Flat export row builders — each returns a plain-object array for xlsx/csv

function singleBarExportRows(rows: TelemetryRow[], field: string) {
  const g: Record<string, number[]> = {};
  rows.forEach(r => {
    if (!r.SummaryDate || r[field] === null) return;
    const d = r.SummaryDate as string;
    g[d] = g[d] ?? [];
    g[d].push(r[field] as number);
  });
  return Object.keys(g).sort().map(date => ({ Date: date, [`Max_${field}`]: Math.max(...g[date]) }));
}

function multiBarExportRows(stations: StationData[], field: string) {
  return stations.flatMap(s =>
    s.rows.filter(r => r.SummaryDate && r[field] !== null)
      .map(r => ({ Station: s.stationName, Date: r.SummaryDate, [field]: r[field] }))
  );
}

function stackedBarExportRows(rows: TelemetryRow[], field: string, threshold: number) {
  const g: Record<string, { Exceedances: number; NonExceedances: number }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate || r[field] === null) return;
    const key = monthYearKey(r.SummaryDate as string);
    g[key] = g[key] ?? { Exceedances: 0, NonExceedances: 0 };
    (r[field] as number) > threshold ? g[key].Exceedances++ : g[key].NonExceedances++;
  });
  return Object.keys(g).map(Month => ({ Month, ...g[Month] }));
}

function pieExportRows(rows: TelemetryRow[], field: string, threshold: number) {
  const measured = rows.filter(r => r[field] !== null);
  const exc = measured.filter(r => (r[field] as number) > threshold).length;
  return [
    { Category: 'Non-Exceedances', Count: measured.length - exc },
    { Category: 'Exceedances',     Count: exc },
  ];
}

function noiseStackedBarExportRows(rows: TelemetryRow[], dayThresh: number, nightThresh: number) {
  const g: Record<string, { Exceedances: number; NonExceedances: number }> = {};
  rows.forEach(r => {
    if (!r.SummaryDate) return;
    const key = monthYearKey(r.SummaryDate as string);
    g[key] = g[key] ?? { Exceedances: 0, NonExceedances: 0 };
    if (r.DayAvg_NoiseLAeq !== null) (r.DayAvg_NoiseLAeq as number) > dayThresh ? g[key].Exceedances++ : g[key].NonExceedances++;
    if (r.NightAvg_NoiseLAeq !== null) (r.NightAvg_NoiseLAeq as number) > nightThresh ? g[key].Exceedances++ : g[key].NonExceedances++;
  });
  return Object.keys(g).map(Month => ({ Month, ...g[Month] }));
}

function noisePieExportRows(rows: TelemetryRow[], dayThresh: number, nightThresh: number) {
  let exc = 0, nonExc = 0;
  rows.forEach(r => {
    if (r.DayAvg_NoiseLAeq !== null) (r.DayAvg_NoiseLAeq as number) > dayThresh ? exc++ : nonExc++;
    if (r.NightAvg_NoiseLAeq !== null) (r.NightAvg_NoiseLAeq as number) > nightThresh ? exc++ : nonExc++;
  });
  return [{ Category: 'Non-Exceedances', Count: nonExc }, { Category: 'Exceedances', Count: exc }];
}

function windroseExportRows(rows: TelemetryRow[]) {
  return rows
    .filter(r => r.SummaryDate && r.Avg_WindDirection)
    .flatMap(r => {
      try {
        const sectors: any[] = JSON.parse(r.Avg_WindDirection as string);
        return sectors.map(s => ({
          Date: r.SummaryDate, Direction: s.Direction,
          Speed_0_0_5_pct: s.Speed1, Speed_0_5_2_pct: s.Speed2, Speed_2_4_pct: s.Speed3,
          Speed_4_6_pct: s.Speed4, Speed_6_8_pct: s.Speed5, Speed_8_10_pct: s.Speed6,
          Speed_10plus_pct: s.Speed7, DataCount: s.DataCount,
        }));
      } catch { return []; }
    });
}

function trendExportRows(rows: TelemetryRow[], fields: string[]) {
  return rows
    .filter(r => r.SummaryDate)
    .sort((a, b) => String(a.SummaryDate).localeCompare(String(b.SummaryDate)))
    .map(r => Object.fromEntries([['Date', r.SummaryDate], ...fields.map(f => [f, r[f]])]));
}

// ─── Fenceline-local export dialog (PNG + SVG) ────────────────────────────────

type FencelineExportSel = { mode: 'graph' | 'graph+data'; format: 'png' | 'svg' };

function FencelineChartExportDialog({ open, title, hasSupportingData, onClose, onConfirm }: {
  open: boolean; title?: string; hasSupportingData: boolean;
  onClose: () => void; onConfirm: (sel: FencelineExportSel) => void;
}) {
  const [mode,   setMode]   = React.useState<'graph' | 'graph+data'>('graph');
  const [format, setFormat] = React.useState<'png' | 'svg'>('png');
  React.useEffect(() => { if (!hasSupportingData) setMode('graph'); }, [hasSupportingData]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 99999 }}>
      <DialogTitle>{title ?? 'Download chart'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel>Content</FormLabel>
            <RadioGroup value={mode} onChange={e => setMode(e.target.value as any)}>
              <FormControlLabel value="graph"      control={<Radio />} label="Graph only" />
              <FormControlLabel value="graph+data" control={<Radio />} label="Graph + supporting data" disabled={!hasSupportingData} />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Image format</FormLabel>
            <RadioGroup value={format} onChange={e => setFormat(e.target.value as any)}>
              <FormControlLabel value="png" control={<Radio />} label="PNG — raster, works in any Word version" />
              <FormControlLabel value="svg" control={<Radio />} label="SVG — vector, crisp at any size (Word 2016+)" />
            </RadioGroup>
          </FormControl>
          {mode === 'graph+data' && hasSupportingData && (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>Supporting data: <b>Excel (.xlsx)</b></Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton variant="contained" sx={{ backgroundColor: '#4BB75F', '&:hover': { backgroundColor: '#3aa451' } }}
          onClick={() => onConfirm({ mode, format })}>
          Download
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── ChartExportWrapper ───────────────────────────────────────────────────────
// Wraps any fenceline chart: stable DOM id → download icon overlay (web-only)
// → FencelineChartExportDialog → exportFencelineChart. Zero impact on other components.

function ChartExportWrapper({ chartId, title, exportRows = [], children }: {
  chartId: string; title: string; exportRows?: any[]; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <View
      style={{ position: 'relative' }}
      {...(Platform.OS === 'web' ? { id: chartId } as any : {})}
    >
      {children}
      {Platform.OS === 'web' && (
        <View
          style={styles.chartExportBtn}
          {...({ 'data-no-export': 'true' } as any)}
        >
          <Tooltip title="Download chart" placement="left" arrow>
            <span><IconButton icon="download" size={18} iconColor="#607D8B" style={{ margin: 0 }} onPress={() => setOpen(true)} /></span>
          </Tooltip>
        </View>
      )}
      {Platform.OS === 'web' && (
        <FencelineChartExportDialog
          open={open}
          title={`Download: ${title}`}
          hasSupportingData={exportRows.length > 0}
          onClose={() => setOpen(false)}
          onConfirm={async sel => {
            setOpen(false);
            await exportFencelineChart(chartId, title, sel.format, sel.mode, exportRows);
          }}
        />
      )}
    </View>
  );
}

// ─── Option A: KPI Conformance Cards (PM10 / PM2.5 / Vibration) ─────────────

function PeriodConformanceCards({ rows, field, threshold }: {
  rows: TelemetryRow[]; field: string; threshold: number;
}) {
  const d = buildPeriodSummaryData(rows, field, threshold);
  if (d.total === 0) return null;
  const period = d.startDate && d.endDate
    ? `${formatDateTick(d.startDate)} – ${formatDateTick(d.endDate)}` : '—';
  return (
    <View style={styles.kpiSection}>
      <ChartTitle title="Period Conformance — KPI Cards" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.kpiRow}>
          <View style={[styles.kpiCard, { borderTopColor: '#1C60A5' }]}>
            <Text style={styles.kpiLabel}>Period</Text>
            <Text style={[styles.kpiValue, { fontSize: 13 }]}>{period}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#607D8B' }]}>
            <Text style={styles.kpiLabel}>Total Samples</Text>
            <Text style={styles.kpiValue}>{d.total}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#388E3C' }]}>
            <Text style={styles.kpiLabel}>Conform</Text>
            <Text style={[styles.kpiValue, { color: '#388E3C' }]}>{d.conform}</Text>
            <Text style={styles.kpiSub}>{d.conformPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: d.nonConform > 0 ? '#C62828' : '#388E3C' }]}>
            <Text style={styles.kpiLabel}>Non-Conform</Text>
            <Text style={[styles.kpiValue, { color: d.nonConform > 0 ? '#C62828' : '#388E3C' }]}>{d.nonConform}</Text>
            <Text style={styles.kpiSub}>{d.nonConformPct}</Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function NoisePeriodConformanceCards({ rows, dayThreshold, nightThreshold }: {
  rows: TelemetryRow[]; dayThreshold: number; nightThreshold: number;
}) {
  const d = buildNoisePeriodSummaryData(rows, dayThreshold, nightThreshold);
  if (d.total === 0) return null;
  const period = d.startDate && d.endDate
    ? `${formatDateTick(d.startDate)} – ${formatDateTick(d.endDate)}` : '—';
  return (
    <View style={styles.kpiSection}>
      <ChartTitle title="Period Conformance —  KPI Cards" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.kpiRow}>
          <View style={[styles.kpiCard, { borderTopColor: '#1C60A5' }]}>
            <Text style={styles.kpiLabel}>Period</Text>
            <Text style={[styles.kpiValue, { fontSize: 13 }]}>{period}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#607D8B' }]}>
            <Text style={styles.kpiLabel}>Total</Text>
            <Text style={styles.kpiValue}>{d.total}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#388E3C' }]}>
            <Text style={styles.kpiLabel}>Conform</Text>
            <Text style={[styles.kpiValue, { color: '#388E3C' }]}>{d.conform}</Text>
            <Text style={styles.kpiSub}>{d.conformPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: d.nonConform > 0 ? '#C62828' : '#388E3C' }]}>
            <Text style={styles.kpiLabel}>Non-Conform</Text>
            <Text style={[styles.kpiValue, { color: d.nonConform > 0 ? '#C62828' : '#388E3C' }]}>{d.nonConform}</Text>
            <Text style={styles.kpiSub}>{d.nonConformPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#FFA726' }]}>
            <Text style={styles.kpiLabel}>Day Conform</Text>
            <Text style={[styles.kpiValue, { color: '#388E3C' }]}>{d.conformDay}</Text>
            <Text style={styles.kpiSub}>{d.conformDayPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: d.nonConformDay > 0 ? '#C62828' : '#FFA726' }]}>
            <Text style={styles.kpiLabel}>Day Non-Conform</Text>
            <Text style={[styles.kpiValue, { color: d.nonConformDay > 0 ? '#C62828' : '#388E3C' }]}>{d.nonConformDay}</Text>
            <Text style={styles.kpiSub}>{d.nonConformDayPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: '#5C6BC0' }]}>
            <Text style={styles.kpiLabel}>Night Conform</Text>
            <Text style={[styles.kpiValue, { color: '#388E3C' }]}>{d.conformNight}</Text>
            <Text style={styles.kpiSub}>{d.conformNightPct}</Text>
          </View>
          <View style={[styles.kpiCard, { borderTopColor: d.nonConformNight > 0 ? '#C62828' : '#5C6BC0' }]}>
            <Text style={styles.kpiLabel}>Night Non-Conform</Text>
            <Text style={[styles.kpiValue, { color: d.nonConformNight > 0 ? '#C62828' : '#388E3C' }]}>{d.nonConformNight}</Text>
            <Text style={styles.kpiSub}>{d.nonConformNightPct}</Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ─── TableScroll — web uses overflow:auto (scrollbar only when needed); native uses ScrollView ──

function TableScroll({ children, contentWidth, onMeasure }: {
  children: React.ReactNode; contentWidth: number; onMeasure: (w: number) => void;
}) {
  const inner = <View style={{ minWidth: contentWidth }}>{children}</View>;
  if (Platform.OS === 'web') {
    return (
      <View
        onLayout={e => onMeasure(e.nativeEvent.layout.width)}
        style={{ width: '100%', overflowX: 'auto' } as any}
      >
        {inner}
      </View>
    );
  }
  return (
    <View onLayout={e => onMeasure(e.nativeEvent.layout.width)}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: contentWidth }}>
        {children}
      </ScrollView>
    </View>
  );
}

// ─── Option B: Single-row period table (PM10 / PM2.5 / Vibration) ────────────

type PeriodRow = { period: string; total: number; conform: number; conformPct: string; nonConform: number; nonConformPct: string };

const PERIOD_CONFORMANCE_COLS: ColDef<PeriodRow>[] = [
  { header: 'Period',           get: r => r.period },
  { header: 'No. of Samples',   get: r => r.total },
  { header: 'Conform No.',      get: r => r.conform },
  { header: 'Conform %',        get: r => r.conformPct },
  { header: 'Non-Conform No.',  get: r => r.nonConform },
  { header: 'Non-Conform %',    get: r => r.nonConformPct },
];

function PeriodConformanceTableRow({ rows, field, threshold, exportFilename }: {
  rows: TelemetryRow[]; field: string; threshold: number; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const d = buildPeriodSummaryData(rows, field, threshold);
  if (d.total === 0) return null;
  const period = d.startDate && d.endDate
    ? `${formatDateTick(d.startDate)} – ${formatDateTick(d.endDate)}` : '—';
  const CW = tableWidth > 0 ? Math.max(90, Math.floor(tableWidth / 6)) : 110;
  const row: PeriodRow = { period, total: d.total, conform: d.conform, conformPct: d.conformPct, nonConform: d.nonConform, nonConformPct: d.nonConformPct };
  const handleExport = () => exportCSV(
    PERIOD_CONFORMANCE_COLS.map(c => c.header),
    [PERIOD_CONFORMANCE_COLS.map(c => c.get(row))],
    exportFilename,
  );
  return (
    <View style={styles.tableSection}>
      <TableToolbar title="Period Conformance" onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, CW * 6)}>
        <View>
          <View style={[styles.tblRow, styles.tblHead]}>
            {PERIOD_CONFORMANCE_COLS.map(c => (
              <Text key={c.header} style={[styles.tblHCell, { width: CW }]}>{c.header}</Text>
            ))}
          </View>
          <View style={styles.tblRow}>
            <Text style={[styles.tblCell, { width: CW }]}>{period}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.total}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conform}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformPct}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConform > 0 && styles.tblAlert]}>{d.nonConform}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConform > 0 && styles.tblAlert]}>{d.nonConformPct}</Text>
          </View>
        </View>
      </TableScroll>
    </View>
  );
}

type NoisePeriodRow = {
  period: string;
  total: number; conform: number; conformPct: string; nonConform: number; nonConformPct: string;
  conformDay: number; conformDayPct: string; nonConformDay: number; nonConformDayPct: string;
  conformNight: number; conformNightPct: string; nonConformNight: number; nonConformNightPct: string;
};

const NOISE_PERIOD_GROUPS: ColGroup<NoisePeriodRow>[] = [
  { group: null, cols: [{ header: 'Period', get: r => r.period, width: 150 }] },
  { group: 'Total', cols: [
    { header: 'No. of Samples',  get: r => r.total },
    { header: 'Conform No.',     get: r => r.conform },
    { header: 'Conform %',       get: r => r.conformPct },
    { header: 'Non-Conform No.', get: r => r.nonConform },
    { header: 'Non-Conform %',   get: r => r.nonConformPct },
  ]},
  { group: 'Day', cols: [
    { header: 'Conform No.',     get: r => r.conformDay },
    { header: 'Conform %',       get: r => r.conformDayPct },
    { header: 'Non-Conform No.', get: r => r.nonConformDay },
    { header: 'Non-Conform %',   get: r => r.nonConformDayPct },
  ]},
  { group: 'Night', cols: [
    { header: 'Conform No.',     get: r => r.conformNight },
    { header: 'Conform %',       get: r => r.conformNightPct },
    { header: 'Non-Conform No.', get: r => r.nonConformNight },
    { header: 'Non-Conform %',   get: r => r.nonConformNightPct },
  ]},
];

function NoisePeriodConformanceTableRow({ rows, dayThreshold, nightThreshold, exportFilename }: {
  rows: TelemetryRow[]; dayThreshold: number; nightThreshold: number; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const d = buildNoisePeriodSummaryData(rows, dayThreshold, nightThreshold);
  if (d.total === 0) return null;
  const period = d.startDate && d.endDate
    ? `${formatDateTick(d.startDate)} – ${formatDateTick(d.endDate)}` : '—';
  const CW = tableWidth > 0 ? Math.max(75, Math.floor((tableWidth - 150) / 13)) : 85;
  const totalW = 150 + CW * 13;
  const row: NoisePeriodRow = {
    period, total: d.total, conform: d.conform, conformPct: d.conformPct,
    nonConform: d.nonConform, nonConformPct: d.nonConformPct,
    conformDay: d.conformDay, conformDayPct: d.conformDayPct,
    nonConformDay: d.nonConformDay, nonConformDayPct: d.nonConformDayPct,
    conformNight: d.conformNight, conformNightPct: d.conformNightPct,
    nonConformNight: d.nonConformNight, nonConformNightPct: d.nonConformNightPct,
  };
  const handleExport = () => exportCSV(flatHeaders(NOISE_PERIOD_GROUPS), [flatRow(NOISE_PERIOD_GROUPS, row)], exportFilename);
  return (
    <View style={styles.tableSection}>
      <TableToolbar title="Period Conformance" onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, totalW)}>
        <View>
          {/* Header row 1 — group labels derived from NOISE_PERIOD_GROUPS */}
          <View style={[styles.tblRow, styles.tblHead]}>
            {NOISE_PERIOD_GROUPS.map((g, gi) => {
              const w = g.cols.reduce((s, c) => s + (c.width ?? CW), 0);
              return (
                <Text key={gi} style={[styles.tblHCell, { width: w }, gi > 0 && { borderLeftWidth: 1, borderLeftColor: '#4A7AB5', textAlign: 'center' } as any]}>
                  {g.group ?? ''}
                </Text>
              );
            })}
          </View>
          {/* Header row 2 — sub-columns derived from NOISE_PERIOD_GROUPS */}
          <View style={[styles.tblRow, { backgroundColor: '#2A6DB5' }]}>
            {NOISE_PERIOD_GROUPS.flatMap((g, gi) => g.cols.map((c, ci) => (
              <Text key={`${gi}-${ci}`} style={[styles.tblHCell, { width: c.width ?? CW }]}>{c.header}</Text>
            )))}
          </View>
          {/* Single data row */}
          <View style={styles.tblRow}>
            <Text style={[styles.tblCell, { width: 150 }]}>{period}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.total}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conform}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformPct}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConform > 0 && styles.tblAlert]}>{d.nonConform}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConform > 0 && styles.tblAlert]}>{d.nonConformPct}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformDay}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformDayPct}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConformDay > 0 && styles.tblAlert]}>{d.nonConformDay}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConformDay > 0 && styles.tblAlert]}>{d.nonConformDayPct}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformNight}</Text>
            <Text style={[styles.tblCell, { width: CW }]}>{d.conformNightPct}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConformNight > 0 && styles.tblAlert]}>{d.nonConformNight}</Text>
            <Text style={[styles.tblCell, { width: CW }, d.nonConformNight > 0 && styles.tblAlert]}>{d.nonConformNightPct}</Text>
          </View>
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Monthly Conformance Table (PM10 / PM2.5 / Vibration) ────────────────────

type MonthlyRow = { month: string; total: number; conform: number; conformPct: string; nonConform: number; nonConformPct: string };

const MONTHLY_CONFORMANCE_COLS: ColDef<MonthlyRow>[] = [
  { header: 'Month',           get: r => r.month },
  { header: 'No. of Samples',  get: r => r.total },
  { header: 'Conform No.',     get: r => r.conform },
  { header: 'Conform %',       get: r => r.conformPct },
  { header: 'Non-Conform No.', get: r => r.nonConform },
  { header: 'Non-Conform %',   get: r => r.nonConformPct },
];

function MonthlyConformanceTable({ rows, field, threshold, exportFilename }: {
  rows: TelemetryRow[]; field: string; threshold: number; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const data = buildMonthlyConformanceData(rows, field, threshold);
  if (data.length === 0) return null;
  const CW = tableWidth > 0 ? Math.max(90, Math.floor(tableWidth / 6)) : 110;
  const handleExport = () => exportCSV(
    MONTHLY_CONFORMANCE_COLS.map(c => c.header),
    data.map(row => MONTHLY_CONFORMANCE_COLS.map(c => c.get(row))),
    exportFilename,
  );
  return (
    <View style={styles.tableSection}>
      <TableToolbar title="Monthly Conformance Summary" onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, CW * 6)}>
        <View>
          <View style={[styles.tblRow, styles.tblHead]}>
            {MONTHLY_CONFORMANCE_COLS.map(c => (
              <Text key={c.header} style={[styles.tblHCell, { width: CW }]}>{c.header}</Text>
            ))}
          </View>
          {data.map((row, i) => (
            <View key={i} style={[styles.tblRow, i % 2 === 1 && styles.tblAlt]}>
              <Text style={[styles.tblCell, { width: CW }]}>{row.month}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.total}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conform}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformPct}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConform > 0 && styles.tblAlert]}>{row.nonConform}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConform > 0 && styles.tblAlert]}>{row.nonConformPct}</Text>
            </View>
          ))}
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Noise Monthly Conformance Table ─────────────────────────────────────────

type NoiseMonthlyRow = {
  month: string; total: number; conform: number; conformPct: string; nonConform: number; nonConformPct: string;
  conformDay: number; conformDayPct: string; nonConformDay: number; nonConformDayPct: string;
  conformNight: number; conformNightPct: string; nonConformNight: number; nonConformNightPct: string;
};

const NOISE_MONTHLY_GROUPS: ColGroup<NoiseMonthlyRow>[] = [
  { group: null, cols: [
    { header: 'Month',          get: r => r.month },
    { header: 'No. of Samples', get: r => r.total },
  ]},
  { group: 'Total', cols: [
    { header: 'Conform No.',     get: r => r.conform },
    { header: 'Conform %',       get: r => r.conformPct },
    { header: 'Non-Conform No.', get: r => r.nonConform },
    { header: 'Non-Conform %',   get: r => r.nonConformPct },
  ]},
  { group: 'Day', cols: [
    { header: 'Conform No.',     get: r => r.conformDay },
    { header: 'Conform %',       get: r => r.conformDayPct },
    { header: 'Non-Conform No.', get: r => r.nonConformDay },
    { header: 'Non-Conform %',   get: r => r.nonConformDayPct },
  ]},
  { group: 'Night', cols: [
    { header: 'Conform No.',     get: r => r.conformNight },
    { header: 'Conform %',       get: r => r.conformNightPct },
    { header: 'Non-Conform No.', get: r => r.nonConformNight },
    { header: 'Non-Conform %',   get: r => r.nonConformNightPct },
  ]},
];

function NoiseMonthlyConformanceTable({ rows, dayThreshold, nightThreshold, exportFilename }: {
  rows: TelemetryRow[]; dayThreshold: number; nightThreshold: number; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const data = buildNoiseMonthlyConformanceData(rows, dayThreshold, nightThreshold);
  if (data.length === 0) return null;
  const CW = tableWidth > 0 ? Math.max(75, Math.floor((tableWidth - 170) / 10)) : 85;
  const totalW = 170 + CW * 10;
  const handleExport = () => exportCSV(
    flatHeaders(NOISE_MONTHLY_GROUPS),
    data.map(row => flatRow(NOISE_MONTHLY_GROUPS, row)),
    exportFilename,
  );
  return (
    <View style={styles.tableSection}>
      <TableToolbar title="Monthly Conformance Summary" onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, totalW)}>
        <View>
          {/* Header row 1 — group labels derived from NOISE_MONTHLY_GROUPS */}
          <View style={[styles.tblRow, styles.tblHead]}>
            {NOISE_MONTHLY_GROUPS.map((g, gi) => {
              const w = g.cols.reduce((s, c) => s + (c.width ?? CW), 0);
              return (
                <Text key={gi} style={[styles.tblHCell, { width: w }, gi > 0 && { borderLeftWidth: 1, borderLeftColor: '#4A7AB5', textAlign: 'center' } as any]}>
                  {g.group ?? ''}
                </Text>
              );
            })}
          </View>
          {/* Header row 2 — sub-columns derived from NOISE_MONTHLY_GROUPS */}
          <View style={[styles.tblRow, { backgroundColor: '#2A6DB5' }]}>
            {NOISE_MONTHLY_GROUPS.flatMap((g, gi) => g.cols.map((c, ci) => (
              <Text key={`${gi}-${ci}`} style={[styles.tblHCell, { width: c.width ?? CW }]}>{c.header}</Text>
            )))}
          </View>
          {data.map((row, i) => (
            <View key={i} style={[styles.tblRow, i % 2 === 1 && styles.tblAlt]}>
              <Text style={[styles.tblCell, { width: CW }]}>{row.month}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.total}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conform}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformPct}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConform > 0 && styles.tblAlert]}>{row.nonConform}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConform > 0 && styles.tblAlert]}>{row.nonConformPct}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformDay}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformDayPct}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConformDay > 0 && styles.tblAlert]}>{row.nonConformDay}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConformDay > 0 && styles.tblAlert]}>{row.nonConformDayPct}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformNight}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.conformNightPct}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConformNight > 0 && styles.tblAlert]}>{row.nonConformNight}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.nonConformNight > 0 && styles.tblAlert]}>{row.nonConformNightPct}</Text>
            </View>
          ))}
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Noise LAeq Summary Table ─────────────────────────────────────────────────

type LAeqRow = {
  dateRange: string;
  totalSamples: number; totalMin: number | null; totalMax: number | null;
  daySamples: number; dayMin: number | null; dayMax: number | null; dayLimits: string;
  nightSamples: number; nightMin: number | null; nightMax: number | null; nightLimits: string;
};

const LAEQ_GROUPS: ColGroup<LAeqRow>[] = [
  { group: null, cols: [{ header: 'Month / Date Range', get: r => r.dateRange, width: 140 }] },
  { group: 'Total', cols: [
    { header: 'No. of Samples', get: r => r.totalSamples },
    { header: 'Min dB(A)',      get: r => fmt(r.totalMin, 3) },
    { header: 'Max dB(A)',      get: r => fmt(r.totalMax, 3) },
  ]},
  { group: 'Day', cols: [
    { header: 'No. of Samples',  get: r => r.daySamples },
    { header: 'Min dB(A)',       get: r => fmt(r.dayMin, 3) },
    { header: 'Max dB(A)',       get: r => fmt(r.dayMax, 3) },
    { header: 'Standard Limits', get: r => r.dayLimits },
  ]},
  { group: 'Night', cols: [
    { header: 'No. of Samples',  get: r => r.nightSamples },
    { header: 'Min dB(A)',       get: r => fmt(r.nightMin, 3) },
    { header: 'Max dB(A)',       get: r => fmt(r.nightMax, 3) },
    { header: 'Standard Limits', get: r => r.nightLimits },
  ]},
];

function NoiseLAeqSummaryTable({ rows, thresholds, exportFilename }: {
  rows: TelemetryRow[]; thresholds: ThresholdsConfig; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const data = buildNoiseLAeqSummaryData(rows, thresholds);
  if (data.length === 0) return null;
  const CW = tableWidth > 0 ? Math.max(75, Math.floor((tableWidth - 140) / 11)) : 90;
  const totalW = 140 + CW * 11;
  const handleExport = () => exportCSV(
    flatHeaders(LAEQ_GROUPS),
    data.map(row => flatRow(LAEQ_GROUPS, row)),
    exportFilename,
  );
  return (
    <View style={styles.tableSection}>
      <TableToolbar title="LAeq Results Summary" onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, totalW)}>
        <View>
          {/* Header row 1 — group labels derived from LAEQ_GROUPS */}
          <View style={[styles.tblRow, styles.tblHead]}>
            {LAEQ_GROUPS.map((g, gi) => {
              const w = g.cols.reduce((s, c) => s + (c.width ?? CW), 0);
              return (
                <Text key={gi} style={[styles.tblHCell, { width: w }, gi > 0 && { borderLeftWidth: 1, borderLeftColor: '#4A7AB5', textAlign: 'center' } as any]}>
                  {g.group ?? ''}
                </Text>
              );
            })}
          </View>
          {/* Header row 2 — sub-columns derived from LAEQ_GROUPS */}
          <View style={[styles.tblRow, { backgroundColor: '#2A6DB5' }]}>
            {LAEQ_GROUPS.flatMap((g, gi) => g.cols.map((c, ci) => (
              <Text key={`${gi}-${ci}`} style={[styles.tblHCell, { width: c.width ?? CW }]}>{c.header}</Text>
            )))}
          </View>
          {data.map((row, i) => (
            <View key={i} style={[styles.tblRow, i % 2 === 1 && styles.tblAlt]}>
              <Text style={[styles.tblCell, { width: 140 }]}>{row.dateRange}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.totalSamples}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.totalMin, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.totalMax, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.daySamples}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.dayMin, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.dayMax, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.dayLimits}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.nightSamples}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.nightMin, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.nightMax, 3)}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.nightLimits}</Text>
            </View>
          ))}
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Daily Concentrations Table ───────────────────────────────────────────────

function DailyConcentrationsTable({ stations, field, threshold, unit, exportFilename, title }: {
  stations: StationData[]; field: string; threshold: number;
  unit: string; exportFilename: string; title: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const stationNames = stations.map(s => s.stationName);
  const tableData = buildDailyTableData(stations, field);
  if (tableData.length === 0) return null;
  const numDynCols = stationNames.length + 4;
  const CW = tableWidth > 0 ? Math.max(75, Math.floor((tableWidth - 210) / numDynCols)) : 80;

  const handleExport = () => {
    const headers = ['Date', ...stationNames, 'Conformance', 'Maximum', 'Minimum', `Limit (${unit})`, 'Average'];
    const rows = tableData.map(row => [
      row.date,
      ...stationNames.map(s => row.stationValues[s] ?? ''),
      row.max !== null && row.max > threshold ? 'Exceedance' : 'Non-Exceedance',
      row.max ?? '', row.min ?? '', threshold, row.avg !== null ? row.avg.toFixed(2) : '',
    ]);
    exportCSV(headers, rows, exportFilename);
  };

  const totalW = 210 + CW * numDynCols;
  return (
    <View style={styles.tableSection}>
      <TableToolbar title={title} onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, totalW)}>
        <View>
          <View style={[styles.tblRow, styles.tblHead]}>
            <Text style={[styles.tblHCell, { width: 90 }]}>Date</Text>
            {stationNames.map(s => <Text key={s} style={[styles.tblHCell, { width: CW }]}>{s}</Text>)}
            <Text style={[styles.tblHCell, { width: 120 }]}>Conformance</Text>
            <Text style={[styles.tblHCell, { width: CW }]}>Maximum</Text>
            <Text style={[styles.tblHCell, { width: CW }]}>Minimum</Text>
            <Text style={[styles.tblHCell, { width: CW }]}>{`Limit (${unit})`}</Text>
            <Text style={[styles.tblHCell, { width: CW }]}>Average</Text>
          </View>
          {tableData.map((row, i) => {
            const isExceedance = row.max !== null && row.max > threshold;
            return (
              <View key={i} style={[styles.tblRow, i % 2 === 1 && styles.tblAlt, isExceedance && styles.tblRowAlert]}>
                <Text style={[styles.tblCell, { width: 90 }]}>{formatDateTick(row.date)}</Text>
                {stationNames.map(s => {
                  const v = row.stationValues[s];
                  const exc = v !== null && v > threshold;
                  return (
                    <Text key={s} style={[styles.tblCell, { width: CW }, exc && styles.tblAlert]}>
                      {v !== null ? v.toFixed(2) : '—'}
                    </Text>
                  );
                })}
                <Text style={[styles.tblCell, { width: 120 }, isExceedance && styles.tblAlert]}>
                  {isExceedance ? 'Exceedance' : 'Non-Exceedance'}
                </Text>
                <Text style={[styles.tblCell, { width: CW }, isExceedance && styles.tblAlert]}>{fmt(row.max)}</Text>
                <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.min)}</Text>
                <Text style={[styles.tblCell, { width: CW }]}>{threshold}</Text>
                <Text style={[styles.tblCell, { width: CW }]}>{fmt(row.avg)}</Text>
              </View>
            );
          })}
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Exceedance Summary Table ─────────────────────────────────────────────────

type ExceedanceRow = {
  stationName: string; parameter: string; daysMeasured: number;
  captureRate: number; exceedances: number; maxValue: number; unit: string; limit: number;
};

const EXCEEDANCE_COLS: ColDef<ExceedanceRow>[] = [
  { header: 'Station',     get: r => r.stationName },
  { header: 'Parameter',   get: r => r.parameter },
  { header: 'Days',        get: r => r.daysMeasured },
  { header: 'Capture %',   get: r => `${r.captureRate}%` },
  { header: 'Exceedances', get: r => r.exceedances },
  { header: 'Max Value',   get: r => `${r.maxValue.toFixed(2)} ${r.unit}` },
  { header: 'Limit',       get: r => `${r.limit} ${r.unit}` },
];

function ExceedanceSummaryTable({ stations, selectedStation, thresholds, title, exportFilename }: {
  stations: StationData[]; selectedStation: string; thresholds: ThresholdsConfig;
  title: string; exportFilename: string;
}) {
  const [tableWidth, setTableWidth] = useState(0);
  const displayStations = selectedStation === 'All'
    ? stations : stations.filter(s => s.stationName === selectedStation);
  const tableRows = displayStations.flatMap(s =>
    buildExceedanceSummary(s, thresholds).map(row => ({ ...row, stationName: s.stationName }))
  );
  if (tableRows.length === 0) return <Text style={styles.noData}>No data to summarise</Text>;
  const CW = tableWidth > 0 ? Math.max(90, Math.floor(tableWidth / 7)) : 115;
  const handleExport = () => exportCSV(
    EXCEEDANCE_COLS.map(c => c.header),
    tableRows.map(row => EXCEEDANCE_COLS.map(c => c.get(row))),
    exportFilename,
  );
  return (
    <View style={styles.tableSection}>
      <TableToolbar title={title} onExport={handleExport} />
      <TableScroll onMeasure={setTableWidth} contentWidth={Math.max(tableWidth, CW * 7)}>
        <View>
          <View style={styles.tblHead}>
            {EXCEEDANCE_COLS.map(c => <Text key={c.header} style={[styles.tblHCell, { width: CW }]}>{c.header}</Text>)}
          </View>
          {tableRows.map((row, i) => (
            <View key={i} style={[styles.tblRow, row.exceedances > 0 && styles.tblRowAlert]}>
              <Text style={[styles.tblCell, { width: CW }]}>{row.stationName}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.parameter}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.daysMeasured}</Text>
              <Text style={[styles.tblCell, { width: CW }, row.captureRate < 85 && styles.tblWarn]}>
                {row.captureRate}%{row.captureRate < 85 ? ' ⚠' : ''}
              </Text>
              <Text style={[styles.tblCell, { width: CW }, row.exceedances > 0 && styles.tblAlert]}>{row.exceedances}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.maxValue.toFixed(2)} {row.unit}</Text>
              <Text style={[styles.tblCell, { width: CW }]}>{row.limit} {row.unit}</Text>
            </View>
          ))}
        </View>
      </TableScroll>
    </View>
  );
}

// ─── Chart components ─────────────────────────────────────────────────────────

function AirQualityChart({ rows, column, threshold, unit }: {
  rows: TelemetryRow[]; column: 'Avg_PM10' | 'Avg_PM2_5'; threshold: number; unit: string;
}) {
  const [containerWidth, setContainerWidth] = useState(400);
  const sorted = [...rows].filter(r => r.SummaryDate).sort((a, b) => String(a.SummaryDate).localeCompare(String(b.SummaryDate)));
  const chartData = sorted.map(r => ({ x: formatDateTick(r.SummaryDate as string), y: r[column] !== null ? (r[column] as number) : null }));
  const exceedances = chartData.filter(d => d.y !== null && (d.y as number) > threshold);
  if (chartData.length === 0) return <Text style={styles.noData}>No data</Text>;
  const chartWidth = Math.max(containerWidth, chartData.length * 22);
  return (
    <View style={styles.chartContainer} onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <VictoryChart width={chartWidth} padding={{ top: 20, bottom: 60, left: 55, right: 20 }}>
          <VictoryAxis style={{ tickLabels: { fontSize: 11, angle: -45, textAnchor: 'end' } }} />
          <VictoryAxis dependentAxis label={unit} style={{ axisLabel: { padding: 42, fontSize: 12 }, tickLabels: { fontSize: 11 } }} />
          <VictoryLine y={() => threshold} style={{ data: { stroke: '#E53935', strokeDasharray: '5,4', strokeWidth: 1.5 } }} />
          <VictoryLine data={chartData.filter(d => d.y !== null)} style={{ data: { stroke: '#1976D2', strokeWidth: 2 } }} />
          {exceedances.length > 0 && <VictoryScatter data={exceedances} size={4} style={{ data: { fill: '#E53935' } }} />}
        </VictoryChart>
      </ScrollView>
    </View>
  );
}

function AirQualityBarCharts({ visibleStations, field, threshold, titlePrefix, unit, idPrefix }: {
  visibleStations: StationData[]; field: 'Avg_PM10' | 'Avg_PM2_5';
  threshold: number; titlePrefix: string; unit: string; idPrefix: string;
}) {
  const combinedRows = visibleStations.flatMap(s => s.rows);
  const singleBar = buildSingleBarData(combinedRows, field);
  const multiBar  = buildMultiBarData(visibleStations, field);
  const stacked   = buildMonthlyExceedances(combinedRows, field, threshold);
  const pie       = buildPieData(combinedRows, field, threshold);
  if (combinedRows.length === 0) return null;
  return (
    <View>
      <ChartTitle title={`${titlePrefix} - Daily Concentrations Max (${unit})`} />
      <ChartExportWrapper chartId={`${idPrefix}-single-bar`} title={`${titlePrefix} Daily Max`} exportRows={singleBarExportRows(combinedRows, field)}>
        <View style={styles.chartCard}><SingleBarChartComp threshold={threshold} data={singleBar} label={singleBar.labels} ChartTitle={`${titlePrefix} Daily Max`} Type="Single" /></View>
      </ChartExportWrapper>
      <ChartTitle title={`${titlePrefix} - Daily Concentrations (${unit})`} />
      <ChartExportWrapper chartId={`${idPrefix}-multi-bar`} title={`${titlePrefix} by Station (Daily)`} exportRows={multiBarExportRows(visibleStations, field)}>
        <View style={styles.chartCard}><MultiBarChartComp threshold={threshold} data={multiBar} label={multiBar.labels} ChartTitle={`${titlePrefix} by Station (Daily)`} Type="Multiple" /></View>
      </ChartExportWrapper>
      <View style={styles.rowContainer}>
        <View style={styles.halfCard}>
          <ChartTitle title={`${titlePrefix} Exceedances (Last 6 Months)`} />
          <ChartExportWrapper chartId={`${idPrefix}-stacked`} title={`${titlePrefix} Monthly Exceedances`} exportRows={stackedBarExportRows(combinedRows, field, threshold)}>
            <StackedBarChartComp data={stacked} label={stacked.labels} ChartTitle={`${titlePrefix} Monthly Exceedances vs Non-Exceedances`} />
          </ChartExportWrapper>
        </View>
        <View style={styles.halfCard}>
          <ChartTitle title={`${titlePrefix} Exceedances (Reporting Period)`} />
          <ChartExportWrapper chartId={`${idPrefix}-pie`} title={`${titlePrefix} Conformance`} exportRows={pieExportRows(combinedRows, field, threshold)}>
            <FencelinePieChartComp data={pie} donutRadius={50} containerWidth={100} legendFontColor="#000" />
          </ChartExportWrapper>
        </View>
      </View>
    </View>
  );
}

function NoiseChart({ rows, dayThreshold, nightThreshold }: {
  rows: TelemetryRow[]; dayThreshold: number; nightThreshold: number;
}) {
  const [containerWidth, setContainerWidth] = useState(400);
  const sorted = [...rows].filter(r => r.SummaryDate).sort((a, b) => String(a.SummaryDate).localeCompare(String(b.SummaryDate)));
  const dayData   = sorted.map(r => ({ x: formatDateTick(r.SummaryDate as string), y: r.DayAvg_NoiseLAeq   !== null ? (r.DayAvg_NoiseLAeq   as number) : null }));
  const nightData = sorted.map(r => ({ x: formatDateTick(r.SummaryDate as string), y: r.NightAvg_NoiseLAeq !== null ? (r.NightAvg_NoiseLAeq as number) : null }));
  const maxData   = sorted.map(r => ({ x: formatDateTick(r.SummaryDate as string), y: r.Max_NoiseLASmax    !== null ? (r.Max_NoiseLASmax    as number) : null }));
  if (sorted.length === 0) return <Text style={styles.noData}>No data</Text>;
  const chartWidth = Math.max(containerWidth, sorted.length * 28);
  return (
    <View style={styles.chartContainer} onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <VictoryChart width={chartWidth} padding={{ top: 20, bottom: 60, left: 55, right: 20 }}>
          <VictoryAxis style={{ tickLabels: { fontSize: 11, angle: -45, textAnchor: 'end' } }} />
          <VictoryAxis dependentAxis label="dB(A)" style={{ axisLabel: { padding: 42, fontSize: 12 }, tickLabels: { fontSize: 11 } }} />
          <VictoryLine y={() => dayThreshold}   style={{ data: { stroke: '#E65100', strokeDasharray: '5,4', strokeWidth: 1.5 } }} />
          <VictoryLine y={() => nightThreshold} style={{ data: { stroke: '#6A1B9A', strokeDasharray: '5,4', strokeWidth: 1.5 } }} />
          <VictoryGroup offset={8} colorScale={['#FFA726', '#5C6BC0']}>
            <VictoryBar data={dayData}   style={{ data: { opacity: 0.85 } }} />
            <VictoryBar data={nightData} style={{ data: { opacity: 0.85 } }} />
          </VictoryGroup>
          <VictoryScatter data={maxData.filter((d: any) => d.y !== null)} size={3} style={{ data: { fill: '#E53935' } }} />
        </VictoryChart>
      </ScrollView>
      <View style={styles.legendRow}>
        <View style={[styles.legendDot, { backgroundColor: '#FFA726' }]} />
        <Text style={styles.legendLabel}>Day LAeq (lim {dayThreshold} dB)</Text>
        <View style={[styles.legendDot, { backgroundColor: '#5C6BC0' }]} />
        <Text style={styles.legendLabel}>Night LAeq (lim {nightThreshold} dB)</Text>
        <View style={[styles.legendDot, { backgroundColor: '#E53935' }]} />
        <Text style={styles.legendLabel}>LAFmax</Text>
      </View>
    </View>
  );
}

function NoiseBarCharts({ visibleStations, dayThreshold, nightThreshold }: {
  visibleStations: StationData[]; dayThreshold: number; nightThreshold: number;
}) {
  const combinedRows = visibleStations.flatMap(s => s.rows);
  const singleDay   = buildSingleBarData(combinedRows, 'DayAvg_NoiseLAeq');
  const singleNight = buildSingleBarData(combinedRows, 'NightAvg_NoiseLAeq');
  const multiDay    = buildMultiBarData(visibleStations, 'DayAvg_NoiseLAeq');
  const multiNight  = buildMultiBarData(visibleStations, 'NightAvg_NoiseLAeq');
  const stacked     = buildNoiseMonthlyExceedances(combinedRows, dayThreshold, nightThreshold);
  const pie         = buildNoisePieData(combinedRows, dayThreshold, nightThreshold);
  if (combinedRows.length === 0) return null;
  return (
    <View>
      <ChartTitle title="Day Noise - Daily Concentrations Max (dB(A))" />
      <ChartExportWrapper chartId="noise-day-single-bar" title="Day Noise Daily Max" exportRows={singleBarExportRows(combinedRows, 'DayAvg_NoiseLAeq')}>
        <View style={styles.chartCard}><SingleBarChartComp threshold={dayThreshold} data={singleDay} label={singleDay.labels} ChartTitle="Noise Daily Max" Type="Single" /></View>
      </ChartExportWrapper>
      <ChartTitle title="Night Noise - Daily Concentrations Max (dB(A))" />
      <ChartExportWrapper chartId="noise-night-single-bar" title="Night Noise Daily Max" exportRows={singleBarExportRows(combinedRows, 'NightAvg_NoiseLAeq')}>
        <View style={styles.chartCard}><SingleBarChartComp threshold={nightThreshold} data={singleNight} label={singleNight.labels} ChartTitle="Noise Daily Max" Type="Single" /></View>
      </ChartExportWrapper>
      <ChartTitle title="Day Noise - Daily Concentrations (dB(A))" />
      <ChartExportWrapper chartId="noise-day-multi-bar" title="Day Noise by Station" exportRows={multiBarExportRows(visibleStations, 'DayAvg_NoiseLAeq')}>
        <View style={styles.chartCard}><MultiBarChartComp threshold={dayThreshold} data={multiDay} label={multiDay.labels} ChartTitle="Noise by Station (Daily)" Type="Multiple" /></View>
      </ChartExportWrapper>
      <ChartTitle title="Night Noise - Daily Concentrations (dB(A))" />
      <ChartExportWrapper chartId="noise-night-multi-bar" title="Night Noise by Station" exportRows={multiBarExportRows(visibleStations, 'NightAvg_NoiseLAeq')}>
        <View style={styles.chartCard}><MultiBarChartComp threshold={nightThreshold} data={multiNight} label={multiNight.labels} ChartTitle="Noise by Station (Daily)" Type="Multiple" /></View>
      </ChartExportWrapper>
      <View style={styles.rowContainer}>
        <View style={styles.halfCard}>
          <ChartTitle title="Noise Exceedances (Last 6 Months)" />
          <ChartExportWrapper chartId="noise-stacked" title="Noise Monthly Exceedances" exportRows={noiseStackedBarExportRows(combinedRows, dayThreshold, nightThreshold)}>
            <StackedBarChartComp data={stacked} label={stacked.labels} ChartTitle="Noise Monthly Exceedances vs Non-Exceedances" />
          </ChartExportWrapper>
        </View>
        <View style={styles.halfCard}>
          <ChartTitle title="Noise Exceedances (Reporting Period)" />
          <ChartExportWrapper chartId="noise-pie" title="Noise Conformance" exportRows={noisePieExportRows(combinedRows, dayThreshold, nightThreshold)}>
            <FencelinePieChartComp data={pie} donutRadius={50} containerWidth={100} legendFontColor="#000" />
          </ChartExportWrapper>
        </View>
      </View>
    </View>
  );
}


function WindRoseSpeedBanded({ rows }: { rows: TelemetryRow[] }) {
  const [containerWidth, setContainerWidth] = useState(400);
  const binnedData = buildWindroseData(rows);
  if (!Object.values(binnedData).some(arr => arr.some(v => v > 0))) return <Text style={styles.noData}>No wind data</Text>;
  return (
    <View style={styles.chartContainer} onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}>
      <Windrose data={binnedData} directionBins={WIND_DIRECTION_BINS} speedRanges={SPEED_RANGES} width={containerWidth} />
    </View>
  );
}

function VibrationChart({ rows, threshold }: { rows: TelemetryRow[]; threshold: number }) {
  const [containerWidth, setContainerWidth] = useState(400);
  const sorted = [...rows].filter(r => r.SummaryDate).sort((a, b) => String(a.SummaryDate).localeCompare(String(b.SummaryDate)));
  const chartData = sorted.map(r => ({ x: formatDateTick(r.SummaryDate as string), y: r.Avg_VibV1 !== null ? (r.Avg_VibV1 as number) : null }));
  const exceedances = chartData.filter(d => d.y !== null && (d.y as number) > threshold);
  if (chartData.length === 0) return <Text style={styles.noData}>No data</Text>;
  const chartWidth = Math.max(containerWidth, chartData.length * 22);
  return (
    <View style={styles.chartContainer} onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <VictoryChart width={chartWidth} padding={{ top: 20, bottom: 60, left: 55, right: 20 }}>
          <VictoryAxis style={{ tickLabels: { fontSize: 11, angle: -45, textAnchor: 'end' } }} />
          <VictoryAxis dependentAxis label="mm/s" style={{ axisLabel: { padding: 42, fontSize: 12 }, tickLabels: { fontSize: 11 } }} />
          <VictoryLine y={() => threshold} style={{ data: { stroke: '#E53935', strokeDasharray: '5,4', strokeWidth: 1.5 } }} />
          <VictoryLine data={chartData.filter(d => d.y !== null)} style={{ data: { stroke: '#43A047', strokeWidth: 2 } }} />
          {exceedances.length > 0 && <VictoryScatter data={exceedances} size={4} style={{ data: { fill: '#E53935' } }} />}
        </VictoryChart>
      </ScrollView>
    </View>
  );
}

function VibrationBarCharts({ visibleStations, threshold, unit }: {
  visibleStations: StationData[]; threshold: number; unit: string;
}) {
  const combinedRows = visibleStations.flatMap(s => s.rows);
  const singleBar = buildSingleBarData(combinedRows, 'Avg_VibV1');
  const multiBar  = buildMultiBarData(visibleStations, 'Avg_VibV1');
  const stacked   = buildMonthlyExceedances(combinedRows, 'Avg_VibV1', threshold);
  const pie       = buildPieData(combinedRows, 'Avg_VibV1', threshold);
  if (combinedRows.length === 0) return null;
  return (
    <View>
      <ChartTitle title={`Vibration - Daily Concentrations Max (${unit})`} />
      <ChartExportWrapper chartId="vib-single-bar" title="Vibration Daily Max" exportRows={singleBarExportRows(combinedRows, 'Avg_VibV1')}>
        <View style={styles.chartCard}><SingleBarChartComp threshold={threshold} data={singleBar} label={singleBar.labels} ChartTitle="Vibration Daily Max" Type="Single" /></View>
      </ChartExportWrapper>
      <ChartTitle title={`Vibration - Daily Concentrations (${unit})`} />
      <ChartExportWrapper chartId="vib-multi-bar" title="Vibration by Station (Daily)" exportRows={multiBarExportRows(visibleStations, 'Avg_VibV1')}>
        <View style={styles.chartCard}><MultiBarChartComp threshold={threshold} data={multiBar} label={multiBar.labels} ChartTitle="Vibration by Station (Daily)" Type="Multiple" /></View>
      </ChartExportWrapper>
      <View style={styles.rowContainer}>
        <View style={styles.halfCard}>
          <ChartTitle title="Vibration Exceedances (Last 6 Months)" />
          <ChartExportWrapper chartId="vib-stacked" title="Vibration Monthly Exceedances" exportRows={stackedBarExportRows(combinedRows, 'Avg_VibV1', threshold)}>
            <StackedBarChartComp data={stacked} label={stacked.labels} ChartTitle="Vibration Monthly Exceedances vs Non-Exceedances" />
          </ChartExportWrapper>
        </View>
        <View style={styles.halfCard}>
          <ChartTitle title="Vibration Exceedances (Reporting Period)" />
          <ChartExportWrapper chartId="vib-pie" title="Vibration Conformance" exportRows={pieExportRows(combinedRows, 'Avg_VibV1', threshold)}>
            <FencelinePieChartComp data={pie} donutRadius={50} containerWidth={100} legendFontColor="#000" />
          </ChartExportWrapper>
        </View>
      </View>
    </View>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FencelineChartsSummary({ siteId, logTitle: _logTitle, isDashboard }: Props) {
  const [allData, setAllData]       = useState<{ columns: string[]; rows: TelemetryRow[] } | null>(null);
  const [stations, setStations]     = useState<StationData[]>([]);
  const [thresholds, setThresholds] = useState<ThresholdsConfig>(DEFAULT_THRESHOLDS);
  const [loading, setLoading]       = useState(false);
  const [dateRange, setDateRange]   = useState<{ start: Date | null; end: Date | null }>(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(start.getDate() - 90);
    return { start, end: today };
  });
  const [selectedStation, setSelectedStation] = useState<string>('All');
  const emptyData = useMemo<any[]>(() => [], []);

  const { getAccessToken } = useAuth();
  const { clientAPIURL }   = useClient();
  const { sitesResp }      = useNav();

  const fetchData = useCallback(async () => {
    if (!sitesResp || !clientAPIURL) return;
    const projectSite = findProjectParent(sitesResp, siteId);
    if (!projectSite) return;
    setLoading(true);
    try {
      const accessToken = await getAccessToken();

      const [telemetryRes, configRes] = await Promise.all([
        fetch(buildTelemetryUrl(clientAPIURL, projectSite.id), {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${clientAPIURL}api/config/${projectSite.id}_complianceConfig`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (configRes.ok) {
        const cfgJson = await configRes.json();
        setThresholds(mapComplianceConfig(cfgJson.configValue));
      }

      if (!telemetryRes.ok) throw new Error(`HTTP ${telemetryRes.status}`);
      const { columns, rows } = await telemetryRes.json() as { columns: string[]; rows: TelemetryRow[] };
      setAllData({ columns, rows });
    } catch (err) {
      console.error('FencelineChartsSummary:', err);
    } finally {
      setLoading(false);
    }
  }, [sitesResp, siteId, clientAPIURL, getAccessToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!allData) return;
    const { columns, rows } = allData;
    const filtered = (() => {
      if (!dateRange.start || !dateRange.end) return rows;
      const s = toLocalDateStr(dateRange.start);
      const e = toLocalDateStr(dateRange.end);
      return rows.filter(r => {
        const d = (r.SummaryDate as string ?? '').split('T')[0];
        return d >= s && d <= e;
      });
    })();
    const grouped = groupByStation(filtered);
    setStations(Object.entries(grouped).map(([name, stationRows]) => ({
      stationName: name,
      rows: stationRows,
      captureRate: calcCaptureRate(columns, stationRows),
    })));
  }, [allData, dateRange]);

  useEffect(() => { setSelectedStation('All'); }, [stations.length]);

  const visibleStations = useMemo(
    () => selectedStation === 'All' ? stations : stations.filter(s => s.stationName === selectedStation),
    [stations, selectedStation]
  );
  const combinedRows = useMemo(() => visibleStations.flatMap(s => s.rows), [visibleStations]);

  const t = thresholds;

  const renderControls = () => (
    <View style={[styles.controls, { zIndex: 200 }]}>
      <View style={styles.contentWrapper}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <View style={{ zIndex: 200 }}>
            <LogDateRangeFilter
              data={emptyData}
              onFilterChange={() => {}}
              onRangeChange={(s, e) => setDateRange({ start: s, end: e })}
              defaultPreset="LAST_3M"
              panelAlign="left"
            />
          </View>
          {stations.length > 1 && (
            <StationSelector stations={stations} selected={selectedStation} onSelect={setSelectedStation} />
          )}
        </View>
      </View>
    </View>
  );

  const renderBody = () => {
    if (loading) return (
      <View style={styles.centred}>
        <ActivityIndicator size="large" color="#1C60A5" />
        <Text style={styles.loadingText}>Loading fenceline data…</Text>
      </View>
    );
    if (stations.length === 0) return (
      <View style={styles.centred}>
        <Text style={styles.noData}>No data available for the selected period.</Text>
      </View>
    );

    if (isDashboard) return (
      <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
        <View style={styles.contentWrapper}>
        {visibleStations.map(s => (
          <View key={s.stationName} style={styles.stationSection}>
            <View style={styles.stationHeader}>
              <Text style={styles.stationTitle}>{s.stationName}</Text>
              <DataCaptureWarningBadge captureRate={s.captureRate} />
            </View>
            <Text style={styles.chartSubtitle}>PM10</Text>
            <ChartExportWrapper chartId={`dash-pm10-${safeId(s.stationName)}`} title={`PM10 – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_PM10'])}>
              <View style={styles.chartCard}><AirQualityChart rows={s.rows} column="Avg_PM10" threshold={t.Avg_PM10.limit} unit={t.Avg_PM10.unit} /></View>
            </ChartExportWrapper>
            <Text style={styles.chartSubtitle}>PM2.5</Text>
            <ChartExportWrapper chartId={`dash-pm2p5-${safeId(s.stationName)}`} title={`PM2.5 – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_PM2_5'])}>
              <View style={styles.chartCard}><AirQualityChart rows={s.rows} column="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} unit={t.Avg_PM2_5.unit} /></View>
            </ChartExportWrapper>
            <Text style={styles.chartSubtitle}>Noise</Text>
            <ChartExportWrapper chartId={`dash-noise-${safeId(s.stationName)}`} title={`Noise – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['DayAvg_NoiseLAeq', 'NightAvg_NoiseLAeq', 'Max_NoiseLASmax'])}>
              <View style={styles.chartCard}><NoiseChart rows={s.rows} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} /></View>
            </ChartExportWrapper>
            <Text style={styles.chartSubtitle}>Wind Speed vs Direction</Text>
            <ChartExportWrapper chartId={`dash-wind-${safeId(s.stationName)}`} title={`Wind Rose – ${s.stationName}`} exportRows={windroseExportRows(s.rows)}>
              <View style={styles.chartCard}><WindRoseSpeedBanded rows={s.rows} /></View>
            </ChartExportWrapper>
            <Text style={styles.chartSubtitle}>Vibration</Text>
            <ChartExportWrapper chartId={`dash-vib-${safeId(s.stationName)}`} title={`Vibration – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_VibV1'])}>
              <View style={styles.chartCard}><VibrationChart rows={s.rows} threshold={t.Avg_VibV1.limit} /></View>
            </ChartExportWrapper>
          </View>
        ))}
        </View>
      </ScrollView>
    );

    return (
      <TabsProvider defaultIndex={0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace>

          <TabScreen label="PM10" icon="air-filter">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                <PeriodConformanceCards rows={combinedRows} field="Avg_PM10" threshold={t.Avg_PM10.limit} />
                <AirQualityBarCharts visibleStations={visibleStations} field="Avg_PM10" threshold={t.Avg_PM10.limit} titlePrefix="PM10" unit={t.Avg_PM10.unit} idPrefix="pm10" />
                <PeriodConformanceTableRow rows={combinedRows} field="Avg_PM10" threshold={t.Avg_PM10.limit} exportFilename="pm10_period_conformance.csv" />
                <MonthlyConformanceTable rows={combinedRows} field="Avg_PM10" threshold={t.Avg_PM10.limit} exportFilename="pm10_monthly_conformance.csv" />
                <DailyConcentrationsTable stations={visibleStations} field="Avg_PM10" threshold={t.Avg_PM10.limit} unit={t.Avg_PM10.unit} exportFilename="pm10_daily.csv" title="PM10 Period daily concentrations for the reporting month" />
                <View style={styles.sectionDivider}>
                  <View style={styles.sectionDividerLine} />
                  <Text style={styles.sectionDividerText}>Per Station Trends</Text>
                  <View style={styles.sectionDividerLine} />
                </View>
                {visibleStations.map(s => (
                  <View key={s.stationName} style={styles.stationSection}>
                    <View style={styles.stationHeader}>
                      <Text style={styles.stationTitle}>{s.stationName}</Text>
                      <DataCaptureWarningBadge captureRate={s.captureRate} />
                    </View>
                    <ChartExportWrapper chartId={`pm10-trend-${safeId(s.stationName)}`} title={`PM10 Trend – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_PM10'])}>
                      <View style={styles.chartCard}><AirQualityChart rows={s.rows} column="Avg_PM10" threshold={t.Avg_PM10.limit} unit={t.Avg_PM10.unit} /></View>
                    </ChartExportWrapper>
                  </View>
                ))}
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="PM2.5" icon="air-filter">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                <PeriodConformanceCards rows={combinedRows} field="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} />
                <AirQualityBarCharts visibleStations={visibleStations} field="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} titlePrefix="PM2.5" unit={t.Avg_PM2_5.unit} idPrefix="pm2p5" />
                <PeriodConformanceTableRow rows={combinedRows} field="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} exportFilename="pm2p5_period_conformance.csv" />
                <MonthlyConformanceTable rows={combinedRows} field="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} exportFilename="pm2p5_monthly_conformance.csv" />
                <DailyConcentrationsTable stations={visibleStations} field="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} unit={t.Avg_PM2_5.unit} exportFilename="pm2_5_daily.csv" title="PM2.5 Period daily concentrations for the reporting month" />
                <View style={styles.sectionDivider}>
                  <View style={styles.sectionDividerLine} />
                  <Text style={styles.sectionDividerText}>Per Station Trends</Text>
                  <View style={styles.sectionDividerLine} />
                </View>
                {visibleStations.map(s => (
                  <View key={s.stationName} style={styles.stationSection}>
                    <View style={styles.stationHeader}>
                      <Text style={styles.stationTitle}>{s.stationName}</Text>
                      <DataCaptureWarningBadge captureRate={s.captureRate} />
                    </View>
                    <ChartExportWrapper chartId={`pm2p5-trend-${safeId(s.stationName)}`} title={`PM2.5 Trend – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_PM2_5'])}>
                      <View style={styles.chartCard}><AirQualityChart rows={s.rows} column="Avg_PM2_5" threshold={t.Avg_PM2_5.limit} unit={t.Avg_PM2_5.unit} /></View>
                    </ChartExportWrapper>
                  </View>
                ))}
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Noise" icon="surround-sound">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                <NoisePeriodConformanceCards rows={combinedRows} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} />
                <NoiseBarCharts visibleStations={visibleStations} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} />
                <NoisePeriodConformanceTableRow rows={combinedRows} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} exportFilename="noise_period_conformance.csv" />
                <NoiseMonthlyConformanceTable rows={combinedRows} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} exportFilename="noise_monthly_conformance.csv" />
                <NoiseLAeqSummaryTable rows={combinedRows} thresholds={t} exportFilename="noise_laeq_summary.csv" />
                <DailyConcentrationsTable stations={visibleStations} field="Avg_NoiseLAeq" threshold={t.DayAvg_NoiseLAeq.limit} unit={t.DayAvg_NoiseLAeq.unit} exportFilename="noise_daily.csv" title="Noise Period daily concentrations for the reporting month" />
                <View style={styles.sectionDivider}>
                  <View style={styles.sectionDividerLine} />
                  <Text style={styles.sectionDividerText}>Per Station Trends</Text>
                  <View style={styles.sectionDividerLine} />
                </View>
                {visibleStations.map(s => (
                  <View key={s.stationName} style={styles.stationSection}>
                    <View style={styles.stationHeader}>
                      <Text style={styles.stationTitle}>{s.stationName}</Text>
                      <DataCaptureWarningBadge captureRate={s.captureRate} />
                    </View>
                    <ChartExportWrapper chartId={`noise-trend-${safeId(s.stationName)}`} title={`Noise Trend – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['DayAvg_NoiseLAeq', 'NightAvg_NoiseLAeq', 'Max_NoiseLASmax'])}>
                      <View style={styles.chartCard}><NoiseChart rows={s.rows} dayThreshold={t.DayAvg_NoiseLAeq.limit} nightThreshold={t.NightAvg_NoiseLAeq.limit} /></View>
                    </ChartExportWrapper>
                  </View>
                ))}
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Wind" icon="weather-windy">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                {visibleStations.map(s => (
                  <View key={s.stationName} style={styles.stationSection}>
                    <Text style={styles.stationTitle}>{s.stationName}</Text>
                    <ChartTitle title="Wind Speed vs Direction" />
                    <ChartExportWrapper chartId={`wind-rose-${safeId(s.stationName)}`} title={`Wind Rose – ${s.stationName}`} exportRows={windroseExportRows(s.rows)}>
                      <View style={styles.chartCard}><WindRoseSpeedBanded rows={s.rows} /></View>
                    </ChartExportWrapper>
                  </View>
                ))}
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Vibration" icon="vibrate">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                <PeriodConformanceCards rows={combinedRows} field="Avg_VibV1" threshold={t.Avg_VibV1.limit} />
                <VibrationBarCharts visibleStations={visibleStations} threshold={t.Avg_VibV1.limit} unit={t.Avg_VibV1.unit} />
                <PeriodConformanceTableRow rows={combinedRows} field="Avg_VibV1" threshold={t.Avg_VibV1.limit} exportFilename="vibration_period_conformance.csv" />
                <MonthlyConformanceTable rows={combinedRows} field="Avg_VibV1" threshold={t.Avg_VibV1.limit} exportFilename="vibration_monthly_conformance.csv" />
                <DailyConcentrationsTable stations={visibleStations} field="Avg_VibV1" threshold={t.Avg_VibV1.limit} unit={t.Avg_VibV1.unit} exportFilename="vibration_daily.csv" title="Vibration Period daily concentrations for the reporting month" />
                <View style={styles.sectionDivider}>
                  <View style={styles.sectionDividerLine} />
                  <Text style={styles.sectionDividerText}>Per Station Trends</Text>
                  <View style={styles.sectionDividerLine} />
                </View>
                {visibleStations.map(s => (
                  <View key={s.stationName} style={styles.stationSection}>
                    <View style={styles.stationHeader}>
                      <Text style={styles.stationTitle}>{s.stationName}</Text>
                      <DataCaptureWarningBadge captureRate={s.captureRate} />
                    </View>
                    <ChartExportWrapper chartId={`vib-trend-${safeId(s.stationName)}`} title={`Vibration Trend – ${s.stationName}`} exportRows={trendExportRows(s.rows, ['Avg_VibV1'])}>
                      <View style={styles.chartCard}><VibrationChart rows={s.rows} threshold={t.Avg_VibV1.limit} /></View>
                    </ChartExportWrapper>
                  </View>
                ))}
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Summary" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={styles.contentWrapper}>
                <View style={styles.stationSection}>
                  <ExceedanceSummaryTable stations={stations} selectedStation={selectedStation} thresholds={t} title="Exceedance Summary" exportFilename="exceedance_summary.csv" />
                </View>
              </View>
            </ScrollView>
          </TabScreen>

        </Tabs>
      </TabsProvider>
    );
  };

  return (
    <View style={logStyles.logRoot}>
      {renderControls()}
      {renderBody()}
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  controls:       { paddingVertical: 10, backgroundColor: '#f7f9fc', borderBottomWidth: 1, borderBottomColor: '#e0e0e0' },
  contentWrapper: { width: '80%', alignSelf: 'center' },
  chip:           { paddingHorizontal: 14, paddingVertical: 7, marginRight: 6, borderRadius: 20, borderWidth: 1, borderColor: '#1C60A5', backgroundColor: '#fff' },
  chipActive:     { backgroundColor: '#1C60A5' },
  chipText:       { fontSize: 12, color: '#1C60A5', fontWeight: '500', fontFamily: 'Inter, system-ui, sans-serif' },
  chipTextActive: { color: '#fff', fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
  dateRow:        { flexDirection: 'row', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  dateField:      { flex: 1, minWidth: 140 },
  dateLabel:      { fontSize: 12, fontWeight: '600', marginBottom: 2, color: '#555', fontFamily: 'Inter, system-ui, sans-serif' },
  datePicker:     { width: '100%' },
  applyBtn:       { backgroundColor: '#2E7D32', borderRadius: 6, paddingHorizontal: 18, paddingVertical: 10 },
  applyBtnText:   { color: '#fff', fontWeight: 'bold', fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif' },

  // Chart card — wraps each chart; provides background, shadow, centering
  chartCard:      { backgroundColor: '#fff', borderRadius: 10, marginVertical: 8, marginHorizontal: 2, padding: 8, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
  // Download icon overlay — positioned top-right of ChartExportWrapper, excluded from PNG capture via data-no-export
  chartExportBtn: { position: 'absolute', top: 4, right: 4, zIndex: 10, backgroundColor: 'rgba(255,255,255,0.85)', borderRadius: 20 },
  // Inner container used by responsive chart functions (full-width, centred)
  chartContainer: { width: '100%', alignItems: 'center' },

  stationSection: { paddingBottom: 12, paddingTop: 4 },
  stationTitle:   { fontSize: 15, fontWeight: 'bold', color: '#1C60A5', marginBottom: 6, paddingBottom: 6, borderBottomWidth: 1.5, borderBottomColor: '#D0E4F7', fontFamily: 'Inter, system-ui, sans-serif' },
  chartSubtitle:  { fontSize: 12, fontWeight: '600', color: '#546E7A', marginTop: 14, marginBottom: 2, paddingLeft: 2, fontFamily: 'Inter, system-ui, sans-serif' },
  chartTitle:     { fontSize: 15, fontWeight: '700', textAlign: 'center', color: '#263238', marginBottom: 4, marginTop: 8, letterSpacing: 0.1, fontFamily: 'Inter, system-ui, sans-serif' },
  rowContainer:   { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 },
  halfCard:       { flex: 1, minWidth: 260, marginBottom: 12 },

  warningBanner:  { backgroundColor: '#FFF8E1', borderLeftWidth: 4, borderLeftColor: '#FFC107', padding: 10, marginVertical: 6, borderRadius: 4 },
  warningTitle:   { fontWeight: 'bold', color: '#5D4037', marginBottom: 2, fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif' },
  warningText:    { fontSize: 12, color: '#5D4037', fontFamily: 'Inter, system-ui, sans-serif' },
  centred:        { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  loadingText:    { marginTop: 10, color: '#666', fontSize: 14, fontFamily: 'Inter, system-ui, sans-serif' },
  noData:         { textAlign: 'center', color: '#aaa', fontSize: 14, padding: 24, fontFamily: 'Inter, system-ui, sans-serif' },
  windLabel:      { fontSize: 11, color: '#888', marginTop: 4, fontFamily: 'Inter, system-ui, sans-serif' },
  legendRow:      { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', paddingBottom: 8, gap: 6 },
  legendDot:      { width: 10, height: 10, borderRadius: 5 },
  legendLabel:    { fontSize: 11, color: '#555', marginRight: 10, fontFamily: 'Inter, system-ui, sans-serif' },

  tableSection:   { paddingBottom: 20 },
  tableToolbar:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 4 },
  exportBtn:      { backgroundColor: '#455A64', borderRadius: 5, paddingHorizontal: 14, paddingVertical: 7, marginBottom: 6 },
  exportBtnText:  { color: '#fff', fontSize: 13, fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
  tblHead:        { flexDirection: 'row', backgroundColor: '#1C60A5' },
  tblRow:         { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eaeff5' },
  tblAlt:         { backgroundColor: '#F5F8FF' },
  tblRowAlert:    { backgroundColor: '#FFF3F3' },
  tblHCell:       { color: '#fff', fontWeight: 'bold', fontSize: 11, paddingHorizontal: 6, paddingVertical: 7, textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif' },
  tblCell:        { fontSize: 11, paddingHorizontal: 6, paddingVertical: 5, color: '#333', textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif' },
  tblAlert:       { color: '#C62828', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' },
  tblWarn:        { color: '#856404', fontFamily: 'Inter, system-ui, sans-serif' },

  // Warning badge (collapsible)
  warnBadge:        { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', backgroundColor: '#FFF8E1', borderWidth: 1, borderColor: '#FFC107', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, marginTop: 4 },
  warnBadgeText:    { fontSize: 11, color: '#856404', fontWeight: '600', fontFamily: 'Inter, system-ui, sans-serif' },
  warnExpanded:     { backgroundColor: '#FFFDE7', borderLeftWidth: 3, borderLeftColor: '#FFC107', borderRadius: 4, padding: 8, marginTop: 4 },
  warnExpandedText: { fontSize: 11, color: '#5D4037', lineHeight: 18, fontFamily: 'Inter, system-ui, sans-serif' },

  // Section divider
  sectionDivider:     { marginTop: 22, marginBottom: 8, flexDirection: 'row', alignItems: 'center' },
  sectionDividerLine: { flex: 1, height: 1, backgroundColor: '#CFD8DC' },
  sectionDividerText: { fontSize: 11, fontWeight: '700', color: '#78909C', paddingHorizontal: 12, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Inter, system-ui, sans-serif' },
  stationHeader:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },

  // KPI card styles
  kpiSection:     { paddingBottom: 12, alignItems: 'center' },
  kpiRow:         { flexDirection: 'row', flexWrap: 'wrap', gap: 10, paddingBottom: 4, justifyContent: 'center' },
  kpiCard:        { backgroundColor: '#fff', borderRadius: 8, borderTopWidth: 4, paddingHorizontal: 14, paddingVertical: 12, minWidth: 110, flex: 1, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 5, elevation: 2 },
  kpiLabel:       { fontSize: 11, color: '#78909C', marginBottom: 4, textAlign: 'center', fontWeight: '500', fontFamily: 'Inter, system-ui, sans-serif' },
  kpiValue:       { fontSize: 22, fontWeight: 'bold', color: '#263238', fontFamily: 'Inter, system-ui, sans-serif' },
  kpiSub:         { fontSize: 12, color: '#90A4AE', marginTop: 2, fontFamily: 'Inter, system-ui, sans-serif' },
});
